// ============================================================================
//  asr.js — public API for the in-browser ASR mini-service.
// ============================================================================
//
//  Audio in → words out. That's the whole contract.
//
//  // 1) Construct once. Backend (WebGPU / WASM-SIMD) is auto-detected.
//  const asr = await createAsr();                       // uses defaults
//  const asr = await createAsr({                        // overrides
//    modelUrl: "./assets/amira-asr_en-us_fastconformer-ft38_int8.onnx",
//    vocabUrl: "./assets/amira-asr_en-us_fastconformer-ft38.vocab.txt",
//    configUrl: "./assets/amira-asr_en-us_fastconformer-ft38.preprocess.json",
//    wasmDir:  "./vendor/",
//    backend:  Backend.Auto,                            // see Backend enum below
//    strideMs: 750,                                     // chunk advance per step
//  });
//
//  // Enum of valid backend choices (use these instead of raw strings):
//  Backend.Auto    === "auto"
//  Backend.WebGpu  === "webgpu"
//  Backend.Wasm    === "wasm"
//
//  // 2) Transcribe. Accepts any of:
//  //      • Float32Array of PCM samples at the model's sample rate
//  //      • AudioBuffer
//  //      • Blob / File   (e.g. a fetched .wav)
//  const { text, words, timings } = await asr.transcribe(input);
//
//  // 3) Inspect:
//  asr.backend;       // "webgpu" | "wasm"
//  asr.sampleRate;    // model's expected sample rate (Hz)
//  asr.windowMs;      // fixed by the model export (≈1000ms)
//  asr.strideMs;      // configurable; <= windowMs
//
//  // 4) Cleanup (optional):
//  await asr.dispose();
//
// ----------------------------------------------------------------------------
//  Chunked decoding
//  ----------------
//  The model is a fixed-window CTC encoder (≈1 second). For longer audio
//  the engine slides a window across the input with the configured stride,
//  runs CTC argmax per window, keeps the middle-of-overlap portion of each
//  window's argmax stream (NeMo `FrameBatchASR` rule), then collapses CTC
//  repeats over the stitched stream.
//
//  • stride_ms == window_ms → no overlap; fastest, boundary words may
//    suffer.
//  • stride_ms < window_ms → overlap-and-stitch; more accurate at the
//    cost of `window/stride` extra inferences per audio second.
//  • A good default for accuracy is stride_ms ≈ 0.75 * window_ms.
//
// ----------------------------------------------------------------------------
//  Notes
//  -----
//  * `words` is `text.split(/\s+/)` with empties dropped.
//  * `timings` is in milliseconds: { mel, inference, decode, total }.
//  * Errors thrown: from createAsr() (model load / fetch failures) and
//    transcribe() (input decode failures). No promise rejections from
//    inference itself — the GPU result is always read back to the CPU.
// ============================================================================

import { detectBackend, configureOrt } from "./backend.js";
import { decodeAudio } from "./audio.js";
import { loadVocab } from "./vocab.js";
import { createEngine } from "./engine.js";
import { runtimeChecks, assertRuntimeReady } from "./runtime_checks.js";
import { TokenPrefixTrie } from "./trie.js";
import { NgramLm } from "./ngram_lm.js";
import { decodeProfileFromPreprocess } from "./model_decode_config.js";

export { decodeProfileFromPreprocess } from "./model_decode_config.js";

const MS_PER_SEC = 1000;

export const Backend = Object.freeze({
  Auto: "auto",
  WebGpu: "webgpu",
  Wasm: "wasm",
});

const BACKEND_VALUES = Object.freeze(new Set(Object.values(Backend)));

// Asset basename encodes language + checkpoint family + epoch + quantization
// so a future search ("which model is this?") starts in the filename rather
// than the file contents. Full provenance lives in the sibling .manifest.json.
const ASSET_BASE = "./assets/amira-asr_en-us_fastconformer-ft38";

const DEFAULTS = {
  modelUrl: `${ASSET_BASE}_int8.onnx`,
  vocabUrl: `${ASSET_BASE}.vocab.txt`,
  configUrl: `${ASSET_BASE}.preprocess.json`,
  wasmDir: "./vendor/",
  backend: Backend.Auto,
  // null => one inference per window (no overlap, lowest CPU/GPU cost).
  // Overlap-and-stitch costs `windowMs / strideMs` inferences per audio
  // second and is required for clean boundary words on long-form audio.
  // For best accuracy callers should set strideMs ≈ 0.75 * windowMs; we
  // leave the default at "fastest" because the demo targets ≤2 s clips
  // where boundary effects are negligible. See "Chunked decoding" above.
  strideMs: null,
};

/**
 * Resolves the hosted ONNX Runtime module URL from the vendor wasm directory.
 * Points at the precompressed `.br` sibling (served with text/javascript +
 * Content-Encoding: br); must match the asrAssetManifest expectedFiles cache key.
 * Only this module adopts `.br` — ORT's own internal fetches (the jsep.* files)
 * use fixed plain filenames relative to `wasmPaths` (see asrAssetManifest.js).
 * @param {string} wasmDir
 * @returns {string}
 */
export function resolveOrtModuleUrl(wasmDir) {
  if (typeof wasmDir !== 'string' || wasmDir.length === 0) {
    throw new TypeError('resolveOrtModuleUrl: wasmDir must be a non-empty string');
  }
  return `${wasmDir.replace(/\/?$/, '/')}ort.mjs.br`;
}

/**
 * Loads the ONNX Runtime ES module from the same versioned asset path as its WASM files.
 * @param {string} wasmDir
 * @returns {Promise<object>}
 */
export async function loadOrtModule(wasmDir) {
  const moduleUrl = resolveOrtModuleUrl(wasmDir);
  return import(/* webpackIgnore: true */ moduleUrl);
}

/**
 * Create an ASR instance. Loads the model and selects a backend.
 * @param {Partial<typeof DEFAULTS>} [opts]
 * @returns {Promise<Asr>}
 */
export async function createAsr(opts = {}) {
  const settings = { ...DEFAULTS, ...opts };
  // Belt-and-suspenders: probe the runtime BEFORE loading the ~20 MB wasm.
  // SIMD is fatal (we only ship the SIMD build); missing threading is a
  // loud warning because the symptom is silent ~3–4× slowdown. See
  // runtime_checks.js for the full gotchas list.
  const checks = runtimeChecks();
  assertRuntimeReady(checks);
  const ort = settings.ortModule || await loadOrtModule(settings.wasmDir);
  configureOrt(ort, settings.wasmDir);
  const backend = await resolveBackend(settings.backend);
  const cfg = await fetchJson(settings.configUrl);
  const decode = decodeProfileFromPreprocess(cfg);
  const vocab = await loadVocab(settings.vocabUrl);
  const strideMs = opts.strideMs !== undefined ? settings.strideMs : decode.strideMs;
  const engine = await createEngine({
    ort,
    modelUrl: settings.modelUrl,
    cfg,
    vocab,
    backend,
    strideMs,
  });
  return new Asr(engine, cfg, checks, vocab, decode);
}

class Asr {
  constructor(engine, cfg, checks, vocab, decode) {
    this._engine = engine;
    this._vocab = vocab;
    this.decode = decode;
    this.backend = engine.backend;
    this.sampleRate = cfg.sample_rate;
    this.windowMs = (engine.windowSamples * MS_PER_SEC) / cfg.sample_rate;
    this.strideMs = (engine.strideSamples * MS_PER_SEC) / cfg.sample_rate;
    // Frozen so callers can render diagnostics in their UI without
    // accidentally mutating it.
    this.runtime = Object.freeze({ ...checks });
  }

  // opts.wordList — passage words (trie beam + snap). Other fields override
  // preprocess.json → decode (see asr.decode after createAsr()).
  async transcribe(input, opts = {}) {
    const { wordList, snapThreshold = this.decode.snapThreshold } = opts;
    const beam = mergeBeamProfile(
      wordList?.length ? this.decode.transcribePassage : this.decode.transcribe,
      opts,
    );

    let beamOpts = null;
    if (wordList && wordList.length > 0) {
      beamOpts = buildPassageBeamOpts(wordList, this._vocab, beam, this.decode.ngram, opts);
    } else if (beam.beamWidth > 1) {
      // Unconstrained beam search (no trie). vocab still required for ▁ word
      // boundaries so per-word wordScores align with the decoded text.
      beamOpts = { beamWidth: beam.beamWidth, topK: beam.topK, trie: null, vocab: this._vocab };
    }

    // Hand the engine a thunk instead of a PCM buffer so that decode
    // (decodeAudioData / OfflineAudioContext.startRendering) runs inside
    // the engine's single-flight queue. Four concurrent UI calls would
    // otherwise allocate four AudioContexts + decoded buffers in parallel,
    // a non-trivial memory burst on low-end devices.
    const { text: rawText, wordScores, timings } = await this._engine.transcribe(
      () => coerceToPcm(input, this.sampleRate),
      beamOpts,
    );

    // Word snapping: map each decoded word to its nearest passage word when
    // the LCS similarity ratio >= snapThreshold. Fixes accent-driven partial
    // decodings like "mi"→"miles", "wa"→"wall" without needing a second
    // inference pass.
    const text = (wordList && wordList.length > 0)
      ? snapWords(rawText, wordList, snapThreshold)
      : rawText;

    // wordScores is a Float32Array aligned to the words in rawText. Word count
    // is unchanged by snapWords (it maps words 1-to-1), so indices stay valid.
    return { text, words: splitWords(text), wordScores, timings };
  }

  // Streaming API for microphone input where the caller manages chunking.
  //
  // const session = asr.startSession({ wordList: ["people", "can", ...] });
  // // for each 1025ms Float32Array from the mic:
  // const { text } = await session.processChunk(pcmChunk);
  // // when the phrase ends:
  // const text = session.finalize();
  //
  // processChunk() accepts any Float32Array length; shorter inputs are
  // zero-padded to the model window internally.  Calls are serialized so
  // concurrent calls are safe — they queue behind each other.
  // finalize() is synchronous: beam state is already current after the
  // last processChunk(), so it just detokenizes and applies word snapping.
  startSession(opts = {}) {
    const { wordList, snapThreshold = this.decode.snapThreshold } = opts;
    const profile = wordList?.length
      ? this.decode.sessionPassage
      : this.decode.session;
    const beam = mergeBeamProfile(profile, opts);

    let beamOpts = null;
    if (wordList && wordList.length > 0) {
      beamOpts = buildPassageBeamOpts(wordList, this._vocab, beam, this.decode.ngram, opts);
    } else if (beam.beamWidth > 1) {
      beamOpts = { beamWidth: beam.beamWidth, topK: beam.topK, trie: null, vocab: this._vocab };
    }

    const inner = this._engine.createSession(beamOpts);
    const vocab = this._vocab;

    return {
      // Returns Promise<{ text, words, wordScores, timings }>
      async processChunk(pcmChunk) {
        const { text: rawText, wordScores, timings } = await inner.processChunk(pcmChunk);
        const text = (wordList && wordList.length > 0)
          ? snapWords(rawText, wordList, snapThreshold)
          : rawText;
        return { text, words: splitWords(text), wordScores, timings };
      },

      // Synchronous — call after the last processChunk() has resolved.
      finalize() {
        const { text: rawText, wordScores } = inner.finalize();
        const text = (wordList && wordList.length > 0)
          ? snapWords(rawText, wordList, snapThreshold)
          : rawText;
        return { text, words: splitWords(text), wordScores };
      },
    };
  }

  async dispose() {
    await this._engine.dispose();
  }
}

function mergeBeamProfile(profile, opts) {
  return {
    beamWidth: opts.beamWidth ?? profile.beamWidth,
    topK:      opts.topK      ?? profile.topK,
    lmAlpha:   opts.lmAlpha   ?? profile.lmAlpha,
    lmBeta:    opts.lmBeta    ?? profile.lmBeta,
  };
}

function mergeNgramOpts(profile, opts) {
  return {
    order:     opts.lmOrder     ?? profile.order,
    weight:    opts.lmWeight    ?? profile.weight,
    smoothing: opts.lmSmoothing ?? profile.smoothing,
  };
}

function buildPassageBeamOpts(wordList, vocab, beam, ngramProfile, opts = {}) {
  const { beamWidth, topK, lmAlpha, lmBeta } = beam;
  const ngramOpts = mergeNgramOpts(ngramProfile, opts);
  const ngramLm = NgramLm.fromWordList(wordList, ngramOpts);
  const trie = TokenPrefixTrie.fromWordList(wordList, vocab, ngramLm.wordToId);
  return { beamWidth, topK, trie, vocab, lmAlpha, lmBeta, ngramLm };
}

function resolveBackend(choice) {
  if (!BACKEND_VALUES.has(choice)) {
    const allowed = [...BACKEND_VALUES].map((v) => `"${v}"`).join(" | ");
    throw new Error(`Unknown backend "${choice}" (use ${allowed} or Backend.*)`);
  }
  if (choice === Backend.Auto) return detectBackend();
  return Promise.resolve(choice);
}

async function fetchJson(url) {
  const resp = await fetch(url);
  if (!resp.ok) throw new Error(`Failed to fetch ${url}: ${resp.status}`);
  return resp.json();
}

function coerceToPcm(input, sampleRate) {
  if (input instanceof Float32Array) return Promise.resolve(input);
  if (input instanceof AudioBuffer) return decodeAudio(input, sampleRate);
  if (input instanceof Blob) return decodeAudio(input, sampleRate);
  throw new TypeError("transcribe() expects Float32Array | AudioBuffer | Blob");
}

function splitWords(text) {
  if (!text) return [];
  return text.split(/\s+/).filter((w) => w.length > 0);
}

// 2 * LCS_length(a, b) / (|a| + |b|) — same formula as difflib.SequenceMatcher.ratio().
// Worst-case O(|a| * |b|); passage words are short so this is cheap per call.
function lcsRatio(a, b) {
  const m = a.length, n = b.length;
  if (m + n === 0) return 1;
  const dp = new Uint16Array((m + 1) * (n + 1));
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i * (n + 1) + j] = a[i - 1] === b[j - 1]
        ? dp[(i - 1) * (n + 1) + (j - 1)] + 1
        : Math.max(dp[(i - 1) * (n + 1) + j], dp[i * (n + 1) + (j - 1)]);
    }
  }
  return (2 * dp[m * (n + 1) + n]) / (m + n);
}

function snapWords(text, passageWords, threshold = 0.55) {
  if (!passageWords || passageWords.length === 0 || !text.trim()) return text;
  return text.split(/\s+/).map((w) => {
    const wl = w.toLowerCase();
    let bestRatio = 0, bestWord = w;
    for (const pw of passageWords) {
      const r = lcsRatio(wl, pw.toLowerCase());
      if (r > bestRatio) { bestRatio = r; bestWord = pw; }
    }
    return bestRatio >= threshold ? bestWord : w;
  }).join(" ");
}
